import { formatDistanceToNowStrict } from 'date-fns';
import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import moment, { Moment, MomentInput, MomentInputObject, unitOfTime } from 'moment';
import momentTz, { MomentInput as MomentTzInput } from 'moment-timezone';
import { match, P } from 'ts-pattern';
import { createDateByArray, parseDate as newGetNewDate } from './datetime';
import { getDateFormat } from './dateRegex';
import {
  ModifyDateUnitCallback,
  MomentIsNow,
  NewDateParams,
  SetDateUnitCallback,
} from './dateTypes';
import { displayTime, ensureMomentTz, getTimeFormatString, setTime } from './time';

export const DATE_FORMAT = 'MM/DD/YYYY';
export const DATE_FORMAT_BILLING = 'YYYY-MM-DD';
export const DATE_FORMAT_FULL = 'ddd MMM DD, YYYY'; // Tue Jan 03, 2023
export const DATE_FORMAT_FULL_MONTH = 'MMMM DD YYYY'; // January 03 2023
export const DATE_FORMAT_FULL_MONTH_WITH_TIME = 'MMMM DD YYYY, LT'; // January 03 2023, 3:14 PM
export const DATE_FORMAT_FULL_WITH_HOURS_MINS_SECONDS = 'MMM DD, YYYY, h:mm:ss A'; // Jan 03, 2023, 3:14:59 PM
export const DATE_FORMAT_FULL_WITH_HOURS_MINS = 'MMM DD, YYYY, h:mm A'; // Jan 03, 2023, 3:14 PM
export const DATE_FORMAT_FULL_WITH_TIME = 'MMM Do YYYY, h:mma';
export const DATE_FORMAT_FULL_WITH_WEEKDAY = 'ddd, MMM DD YYYY, LT'; // Thu, Apr 20 2023, 10:05 AM
export const DATE_FORMAT_FULL_WITH_WEEKDAY_2 = 'ddd, MMM DD, YYYY, LT'; // Thu, Apr 20, 2023, 10:05 AM
export const DATE_FORMAT_LABEL = 'MMM DD, YYYY LTS';
export const DATE_FORMAT_TIME_HOURS_MINS = 'h:mm A';
export const DATE_FORMAT_TRUNC_YEAR = 'MM/DD/YY';
export const DATE_FORMAT_WITH_COMMA_HOUR_MINS = 'M/DD/YYYY, h:mm A';
export const DATE_FORMAT_WITH_COMMA_HOUR_MINS_WITH_TIMEZONE = 'M/DD/YYYY, h:mm A z';
export const DATE_FORMAT_WITH_HOUR_MINS = 'M/DD/YYYY h:mma';
export const DATE_FORMAT_WITH_TIME = 'M/DD/YYYY ha';
export const DATE_FORMAT_MONTH_DAY = 'MMM D';
export const DATE_FORMAT_DAY_NUMBERIC = 'D';
export const CAMPAIGN_DATE_FORMAT = 'llll'; // ex: Mon, Mar 6, 2023 1:56 AM
export const CAMPAIGN_DATE_FORMAT_WITH_TZ = 'llll z'; // ex: Mon, Mar 6, 2023 1:56 AM PST
export const CAMPAIGN_FULL_DATE_FORMAT = 'dddd, MMMM Do YYYY [at] hh:mm A'; // Monday, July 31st 2023 at 12:00 AM
export const CAMPAIGN_FULL_DATE_FORMAT_WITH_TZ = 'dddd, MMMM Do YYYY [at] hh:mm A z'; // Monday, July 31st 2023 at 12:00 AM PST
export const ES_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const ES_DATE_FORMAT_WITH_TIMEZONE = 'YYYY-MM-DD HH:mm:ss ZZ';
export const FILENAME_DATE_FORMAT = 'MM_DD_YYYY_hh_mmA';
export const TIMEZONE_FORMAT = 'Z z';
export const TIMEZONE_ABBREVIATION = 'zz'; // ie. PDT or MDT
export const DATE_AT_TIME = 'MM/DD/YYYY [at] h:mm A';
export const QUERY_BUILDER_DATE_FORMAT = 'M/DD/YYYY h:mma z'; // ie: 4/25/1992 12:43pm PDT

export const MONTH = 'month';
export const DAY = 'day';
export const DATE = 'date';
export const WEEK = 'week';
export const YEAR = 'year';

export const START_DATE = 'startDate';
export const END_DATE = 'endDate';

export const INVALID = 'invalid';

export const MILLISECONDS_IN_DAY = 86400000;

// Util functions to de-momentize date functions

const fromMoment = (value: Moment): Date => ensureMomentTz(value).toDate();

const castToNumber = (value: string | number) =>
  typeof value === 'number' ? value : Number.parseInt(value, 10);

const fromMomentArray = (value: (string | number)[]): Date =>
  pipe(
    value,
    array.map(castToNumber),
    array.takeLeftWhile((v) => !Number.isNaN(v)), // trim array from the first NaN value
    createDateByArray,
  );

const fromMomentObject = (value: MomentInputObject): Date => {
  const year = value.year ?? value.years ?? value.y;
  const month = value.month ?? value.months ?? value.M;
  const day = value.day ?? value.days ?? value.d ?? value.date ?? value.dates ?? value.D;
  const hour = value.hour ?? value.hour ?? value.h;
  const minute = value.minute ?? value.minutes ?? value.m;
  const second = value.second ?? value.seconds ?? value.s;
  const ms = value.millisecond ?? value.milliseconds ?? value.ms;

  return pipe(
    [year, month, day, hour, minute, second, ms],
    array.map(option.fromNullable), // convert to Option<number>
    array.takeLeftWhile(option.isSome), // trim the array from the first empty value
    array.compact, // convert back to number
    createDateByArray,
  );
};

const fromMomentInput = (value: MomentInput): Date =>
  match(value)
    .when(moment.isMoment, fromMoment)
    .with(P.string, P.number, (v) => newGetNewDate(v))
    .with(P.array(P._), fromMomentArray)
    .with(P.instanceOf(Date), (v) => v)
    .with(P.nullish, () => newGetNewDate())
    .otherwise(fromMomentObject);

const toMoment = (value: Date): Moment => moment(value);

const forceMomentTz = (timeZone?: string) => (value: Moment) => {
  if (!timeZone) {
    return value;
  }

  const newDate = ensureMomentTz(value);
  return newDate.tz(timeZone);
};

const forceIsNow =
  (isNow?: boolean) =>
  (value: Moment): MomentIsNow => {
    const newValue: MomentIsNow = value.clone();
    if (isNow) {
      newValue.isNow = true;
    }

    return newValue;
  };

// Moment-based date functions to be deprecated

/**
 * @deprecated Use `getNewDate` from `utils/datetime`
 */
export const getNewDate = ({ date, isNow, timeZone }: NewDateParams = {}) =>
  pipe(
    date,
    fromMomentInput, // convert input to a Date object
    toMoment, // convert back to moment
    forceMomentTz(timeZone),
    forceIsNow(isNow), // tack on the isNow value if requested
  );

/**
 * Formats date to readable format.
 * @deprecated Use `localizeDateTime` from `utils/l10n`
 */
export const displayDate = (date: MomentInput, formatString: string = DATE_FORMAT) =>
  moment(date).format(formatString);

/**
 * Formats date to readable format.
 * @deprecated Use `localizeDateTime` from `utils/l10n`
 */
export const displayDateTz = (
  date: MomentInput,
  formatString: string = DATE_FORMAT,
  timezone?: string,
) =>
  timezone
    ? momentTz.tz(date as MomentTzInput, timezone).format(formatString)
    : moment.utc(date).format(formatString);
/**
 * Formats date to a readable format. Similar to `displayDate`, this function
 * also prepends "Today"/"Yesterday" to date strings that are 0-1 days in the
 * past.
 *
 * @deprecated Use `localizeDateTime` from `utils/l10n`
 */
export const displayDateWithTodayAndYesterdayPrefix = (date: MomentInput) => {
  const TODAY = moment().clone().startOf('day');
  const YESTERDAY = moment().clone().subtract(1, 'days').startOf('day');

  const time = 'h:mm A';
  const isToday = moment(date).isSame(TODAY, 'd');
  const isYesterday = moment(date).isSame(YESTERDAY, 'd');

  if (isToday) {
    return `Today, ${displayDate(date, time)}`;
  }
  if (isYesterday) {
    return `Yesterday, ${displayDate(date, time)}`;
  }
  return displayDate(date, DATE_FORMAT_WITH_COMMA_HOUR_MINS);
};

/**
 * Formats date to a readable format. This function also prepends to date strings:
 * "Today"/"Yesterday"/"X day(s) ago"/"X month(s) ago"/"X year(s) ago".
 * The largest time unit prefix will be returned.
 *
 * @deprecated Use `localizeRelativeTimeSince` from `utils/l10n`
 */
export const displayDateWithDayWeekMonthYearPrefix = (date: Date | number, withTime = true) => {
  const timeDiffs = {
    year: formatDistanceToNowStrict(date, { unit: 'year', roundingMethod: 'floor' }),
    month: formatDistanceToNowStrict(date, { unit: 'month', roundingMethod: 'floor' }),
    day: formatDistanceToNowStrict(date, { unit: 'day', roundingMethod: 'floor' }),
  };

  const dateDisplayTime = displayDate(date, DATE_FORMAT_TIME_HOURS_MINS);

  const timeDiffMessages = Object.values(timeDiffs).reduce((messages: string[], value: string) => {
    if (value === '0 days') messages.push(`Today${withTime ? `, ${dateDisplayTime}` : ''}`);
    else if (value === '1 day') messages.push(`Yesterday${withTime ? `, ${dateDisplayTime}` : ''}`);
    else if (value.charAt(0) !== '0') messages.push(`${value} ago`);
    return messages;
  }, []);

  /** Only return the difference of the LARGEST time unit */
  return timeDiffMessages[0];
};

/**
 * Formats date to readable format.
 * @deprecated Use `localizeDateRange` from `utils/l10n`
 */
export const displayDateRange = (
  startDate: MomentInput,
  endDate: MomentInput,
  showTime?: boolean,
  twentyFourHour?: boolean,
  hasSeconds?: boolean,
  fullDate = true,
  timeZone?: string,
) => {
  const showTimeZone = typeof timeZone === 'string';
  const timeZoneFormat = showTimeZone ? ` (${TIMEZONE_FORMAT})` : '';
  const dateFormatString = fullDate ? DATE_FORMAT_FULL : DATE_FORMAT;
  const endDateWithTimeZone = momentTz(endDate as MomentTzInput).tz(
    timeZone || momentTz.tz.guess(),
  );
  let dateFormat = dateFormatString;
  let dateFormatWithTimezone = `${dateFormatString}${timeZoneFormat}`;
  if (showTime) {
    dateFormat = `${dateFormatString} ${getTimeFormatString(twentyFourHour, hasSeconds)}`;
    dateFormatWithTimezone = `${dateFormat}${timeZoneFormat}`;
  }
  return `${displayDate(startDate, dateFormat)} - ${displayDate(
    endDateWithTimeZone,
    dateFormatWithTimezone,
  )}`;
};

/**
 * **combineDateAndTime**_(date, time, twentyFourHour, hasSeconds)_<br>
 * Combines the date and time from two moment time objects.
 * @param { Moment } date: The moment date object.
 * @param { Moment } time: The moment time object.
 * @param { Boolean } twentyFourHour: Formats the time to 24-Hour clock.
 * @param { Boolean } hasSeconds: Time contains seconds.
 * @returns { Moment } The combined date and time moment object.
 *
 * @deprecated use `combineDateAndTime` from `utils/datetime`
 */
export function combineDateAndTime(): null;
export function combineDateAndTime<T = Moment>(
  date: undefined,
  time: T,
  twentyFourHour?: boolean,
  hasSeconds?: boolean,
): T;
export function combineDateAndTime<T = Moment>(
  date: T,
  time?: undefined,
  twentyFourHour?: boolean,
  hasSeconds?: boolean,
): T;
export function combineDateAndTime(
  date: Moment,
  time: Moment,
  twentyFourHour?: boolean,
  hasSeconds?: boolean,
): Moment;
export function combineDateAndTime(
  date?: any,
  time?: any,
  twentyFourHour = false,
  hasSeconds = false,
) {
  if (date && time) {
    return setTime(date, displayTime(time), twentyFourHour, hasSeconds);
  } else if (date) {
    return date;
  } else if (time) {
    return time;
  }
  return null;
}

/**
 * **setDateUnit**_(date, amount, unit)_<br>
 * Set the value of a date unit.
 * @deprecated
 * @safeToDelete
 */
export const setDateUnit = (date: MomentInput, amount: number, unit: keyof Moment = DATE) =>
  (moment(date)[unit] as SetDateUnitCallback)(amount);

/**
 * **setDate**_(currentDate, newDate)_<br>
 * Parses a given string or date and sets it to current date.
 * @param { Moment | String } currentDate: The current Moment date object.
 * @param { String } newDate: Formatted string of new date to set.
 * @returns { Moment } updated time.
 * @deprecated Use `setDate` from `utils/datetime`
 */
export function setDate(currentDate: MomentInput): string;
export function setDate(currentDate: MomentInput, newDate: string): Moment;
export function setDate(currentDate: MomentInput, newDate?: string) {
  let changedDate: Moment | string = '';

  if (newDate && newDate.split) {
    const dateArray = newDate.split('/').map((value) => parseInt(value, 10));
    changedDate = moment(currentDate)
      .year(dateArray[2])
      .month(dateArray[0] - 1)
      .date(dateArray[1]);
  }
  return changedDate;
}

/**
 * **formatDate**_(input)_<br>
 * From a given input, format to readable date.
 * @param { String } input: Date input to format.
 * @returns { Moment | String } The formatted date.
 * @deprecated Use `localizeDateTime` from `utils/l10n`
 */
export const formatDate = (input: string) => {
  let output = input.trim();

  const { formatString } = getDateFormat(output);
  if (formatString) {
    output = displayDate(moment(output, formatString));
  } else if (input === '') {
    output = '';
  } else {
    output = INVALID;
  }
  return output;
};

// #Todo - not migrated to datetime
/**
 * **formatDateForSave**_(date, format)_<br>
 * Parses date to save format.
 * @param { moment } date: The moment date object to format to save format.
 * @param { string } format: The format string of the date.
 * @returns { Number } The parsed date to save format.
 * @deprecated Use `convertToString` from `utils/datetime`
 */
export const formatDateForSave = (date?: Moment | null, format?: string) => {
  if (date) {
    if (format) {
      return displayDate(date, format);
    }
    return moment(date).valueOf();
  }
  return null;
};

/**
 * **parseDateFromSave**_(date)_<br>
 * Parses date from save format.
 * @param { string } date: The string save date format to moment.
 * @returns { Number } The parsed date string to moment.
 * @deprecated Use `getNewDate` from `utils/datetime`
 */
export const parseDateFromSave = (date?: string | number | null) => (date ? moment(date) : null);

/**
 * Parses a date string that is formatted in the zone-extended
 * ISO-8601 format (i.e. "2011-12-03T10:15:30+01:00[Europe/Paris]").
 *
 * More info: https://docs.oracle.com/javase/8/docs/api/java/time/format/DateTimeFormatter.html#ISO_ZONED_DATE_TIME
 *
 * @param {string} date: An zone-extended ISO-8601 date string
 * @deprecated Use `getNewDate` from `utils/datetime`
 */
export const parseISOZonedDate = (date: string) => {
  const indexOfTimezoneBracket = date?.indexOf('[');
  if (indexOfTimezoneBracket === -1) {
    return momentTz(date);
  }

  const timestamp = date?.slice(0, indexOfTimezoneBracket);
  const timezoneId = date?.slice(indexOfTimezoneBracket + 1, -1);
  return momentTz.tz(timestamp, timezoneId);
};

/**
 * **modifyDateUnit**_(date, add, amount, unit)_<br>
 * Adds or subtracts from date based on units.
 * @param { Moment } date: Moment date object to add/subtract from.
 * @param { Boolean } add: true: Add to time. false: subtract from date.
 * @param { Number } amount: Amount of units to add/subtract from date.
 * @param { String } unit: Unit to add/subtract from date.
 * @returns { Moment } Updated date.
 * @deprecated Use `modifyDateUnit` from `utils/datetime`
 */
export const modifyDateUnit = (date: MomentInput, add = true, amount = 1, unit: string = DAY) =>
  (moment(date)[add ? 'add' : 'subtract'] as ModifyDateUnitCallback)(amount, unit);

/**
 * @deprecated
 * @safeToDelete
 */
export const timestampToShortDate = (timestamp: number | string) =>
  moment(timestamp).format(DATE_FORMAT_WITH_TIME);

/**
 * @deprecated Use `localizeDateTime` from `utils/l10n`
 */
export const timestampToShortDateHourMin = (timestamp: number | string) =>
  moment(timestamp).format(DATE_FORMAT_WITH_HOUR_MINS);

/**
 * @deprecated
 * @safeToDelete
 */
export const isDateStringValid = (date: string) => formatDate(date) !== INVALID;

/**
 * **isAfterDate**_(date, base)_<br>
 * Determine if given date is after today.
 * @param { Moment } date: The moment date object to check.
 * @param { Object } base: The moment date object to compare to. Defaults to now.
 * @returns { Boolean } if given date object occurs after today.
 * @deprecated Use `isDateBefore` from `utils/datetime`
 */
export const isDateBefore = (date?: MomentInput, base: MomentInput = moment()) =>
  moment(date).isBefore(base, 'day');

/**
 * **isAfterDate**_(date, base)_<br>
 * Determine if given date is after today.
 * @param { Moment } date: The moment date object to check.
 * @param { Moment } base: The moment date object to compare to. Defaults to now.
 * @returns { Boolean } if given date object occurs after today.
 * @deprecated Use `isDateAfter` from `utils/datetime`
 */
export const isDateAfter = (date?: MomentInput, base: MomentInput = moment()) =>
  moment(date).isAfter(base, 'day');

/**
 * **isDateInRange**_(date, start, end)_<br>
 * Determine if given date is within a date range.
 * @param { Moment } date: The moment date object to check.
 * @param { Moment } start: The start date of the range.\
 * @param { Moment } end: The end date of the range.
 * @returns { Boolean } if given date object is withing the given date range.
 * @deprecated Use `isDateBetween` from `utils/datetime`
 */
export const isDateInRange = (date?: MomentInput, start?: MomentInput, end?: MomentInput) => {
  let isDayBefore = false;
  let isDayAfter = false;

  if (start) {
    isDayBefore = isDateBefore(date, start);
  }
  if (end) {
    isDayAfter = isDateAfter(date, end);
  }
  return !isDayBefore && !isDayAfter;
};

/**
 * **getDatesAgo**_(startDate, amount, unit)_<br>
 * Get calculated date units ago.
 * @param { Moment } startDate: The starting date to calculate relative dates from.
 * @param { Number } amount: The amount of date units ago.
 * @param { String } unit: The date unit.
 * @returns { Moment } The relative date.
 */
export const getDatesAgo = (
  startDate: MomentInput,
  amount = 1,
  unit: unitOfTime.DurationConstructor = DAY,
) => moment(startDate).subtract(amount, unit);

/**
 * **getDaysAgo**_(startDate, amount)_<br>
 * Get calculated days ago.
 * @param { Moment } startDate: The starting date to calculate relative days from.
 * @param { Number } amount: The amount of days ago.
 * @returns { Moment } The relative date.
 * @deprecated use `getDaysAgo` from `utils/datetime`
 */
export const getDaysAgo = (startDate: MomentInput, amount = 1) => getDatesAgo(startDate, amount);

/**
 * @param { string } time - a moment-convertable string
 * @returns { string } - a human readable string with the offeset
 * @deprecated Use `localizeRelativeTimeSince` from `utils/l10n`
 */
export const getFromNow = (time: MomentInput): string => moment(time).fromNow();

/**
 * **getWeeksAgo**_(startDate, amount)_<br>
 * Get calculated weeks ago.
 * @param { Object } startDate: The starting date to calculate relative weeks from.
 * @param { Number } amount: The amount of weeks ago.
 * @returns { Object } The relative date.
 * @deprecated use `getWeeksAgo` from `utils/datetime`
 */
export const getWeeksAgo = (startDate: MomentInput, amount = 1) =>
  getDatesAgo(startDate, amount, WEEK);

/**
 * **getMonthsAgo**_(startDate, amount)_<br>
 * Get calculated months ago.
 * @param { Moment } startDate: The starting date to calculate relative months from.
 * @param { Number } amount: The amount of months ago.
 * @returns { Moment } The relative date.
 * @deprecated use `getMonthsAgo` from `utils/datetime`
 */
export const getMonthsAgo = (startDate: MomentInput, amount = 1) =>
  getDatesAgo(startDate, amount, MONTH);

/**
 * **stripLeadingZeros**_(date)_<br>
 * Removes leading zeros from single digit month and dates of date string
 * @param { String } date: Display date string (i.e. MM/dd/YYYY)
 * @returns {String } Date string without leading zeros
 * @deprecated This should no longer be used
 */
export const stripLeadingZeros = (date: string) => {
  const [month, days, year] = date.split('/');
  const stripMonth = month.charAt(0) === '0' ? month.charAt(1) : month;
  const stripDays = days.charAt(0) === '0' ? days.charAt(1) : days;
  return [stripMonth, stripDays, year].join('/');
};

/**
 * **stripTrailingTimezone**_(datetime)_<br>
 * Removes leading zeros from single digit month and dates of date string
 * @param { String } date: ISO datetime string with timezone prefix (i.e. YYYY-MM-DDTHH:mm:ss.sssZ[Etc/UTC])
 * @returns {String } ISO datetime string without timezone prefix
 */
export const stripTimezonePrefix = (date: string) => {
  return date.replace(/\[.*\]/, '');
};

/**
 * Definition of a few time presets to show on calendar.
 */
export const presets = {
  TODAY: {
    label: 'Today',
    format: () => ({ startDate: getNewDate().startOf(DAY), endDate: getNewDate({ isNow: true }) }),
  },
  YESTERDAY: {
    label: 'Yesterday',
    format: () => ({
      startDate: getDaysAgo(getNewDate(), 1).startOf(DAY),
      endDate: getDaysAgo(getNewDate(), 1).endOf(DAY),
    }),
  },
  PAST_3_DAYS: {
    label: 'Past 3 Days',
    format: () => ({
      startDate: getDaysAgo(getNewDate(), 3).startOf(DAY),
      endDate: getNewDate({ isNow: true }),
    }),
  },
  PAST_5_DAYS: {
    label: 'Past 5 Days',
    format: () => ({
      startDate: getDaysAgo(getNewDate(), 5).startOf(DAY),
      endDate: getNewDate({ isNow: true }),
    }),
  },
  PAST_WEEK: {
    label: 'Past Week',
    format: () => ({
      startDate: getWeeksAgo(getNewDate(), 1).startOf(DAY),
      endDate: getNewDate({ isNow: true }),
    }),
  },
  PAST_MONTH: {
    label: 'Past Month',
    format: () => ({
      startDate: getMonthsAgo(getNewDate(), 1).startOf(DAY),
      endDate: getNewDate({ isNow: true }),
    }),
  },
};

/**
 * **recencyInDays**_(date)_<br>
 * Get recency in days.
 * @param { Moment } date: Past date to calculate recency of.
 * @returns { Number } The number of days ago.
 * @deprecated Use `getRecencyInDays` from `utils/datetime`
 */
export const recencyInDays = (date: MomentInput) =>
  (getNewDate().valueOf() - moment(date).valueOf()) / MILLISECONDS_IN_DAY;

/**
 * **formatMomentInput**_(str)_<br>
 * Format a string to be a valid Moment input.
 * @param { String } str: Date string in one of a few formats commonly returned from the BE.
 * @returns { String } Date string that represents a valid Moment input.
 * @deprecated Use `convertToString` from `utils/datetime`
 */
export const formatMomentInput = (str: string) => {
  const regex =
    /(\d{4}-[01]\d-[0-3]\d)[\sT]*([0-2]\d:[0-5]\d:[0-5]\d\.?\d{0,3})[\s]*([+-][0-2]\d:[0-5]\d)/g;
  return str.replace(regex, '$1T$2$3');
};
